<template>
  <div v-if="isAdmin">
    <q-table
      title="Reviews"
      row-key="id"
      :data="courseRatings"
      :columns="columns"
      :loading="loading"
      :pagination="initialPagination"
      :rows-per-page-options="[25, 50, 150, 250, 500, 1000, 0]"
      separator="cell"
      flat
      bordered
      binary-state-sort
    >
      <template v-slot:body-cell-comments="props">
        <q-td :props="props">
          <div class="comments">{{ props.row.comments }}</div>
        </q-td>
      </template>
      <template v-slot:body-cell-courseName="props">
        <q-td :props="props">
          <router-link :to="'/course/' + props.row.courseId">{{
            props.row.courseName
          }}</router-link>
        </q-td>
      </template>
    </q-table>
  </div>
</template>
<script>
import { mapGetters, mapState } from "vuex";
import {
  getCourseRatingAttributesMeta,
  getAllCourseRatings,
} from "@/apis/course-rating-service";
import { sortDate } from "@/utils";

export default {
  name: "Reviews",
  data() {
    return {
      loading: false,
      loadError: null,
      initialPagination: {
        sortBy: "createdAt",
        descending: true,
        rowsPerPage: 25,
      },
      columns: [
        {
          name: "createdAt",
          required: true,
          label: "Created",
          align: "left",
          field: row => row.createdAt,
          format: (val, row) => row.createdAtFormatted,
          sortable: true,
          sort: sortDate,
        },
        {
          name: "displayName",
          required: true,
          label: "Display Name",
          align: "left",
          field: row => row.displayName,
          format: val => (val ? val : "-"),
          sortable: true,
        },
        {
          name: "courseName",
          required: true,
          label: "Course",
          align: "left",
          field: row => row.courseName,
          format: val => (val ? val : "-"),
          sortable: true,
        },
        {
          name: "datePlayed",
          required: true,
          label: "Date Played",
          align: "center",
          field: row => row.datePlayed,
          format: (val, row) => row.datePlayedFormatted,
          sortable: true,
          sort: sortDate,
        },
        {
          name: "rating_Course",
          required: true,
          label: "Course",
          align: "center",
          field: row => row.rating_Course,
          format: val => (val ? val : "-"),
          sortable: true,
        },
        {
          name: "rating_Conditions",
          required: true,
          label: "Conditions",
          align: "center",
          field: row => row.rating_Conditions,
          format: val => (val ? val : "-"),
          sortable: true,
        },
        {
          name: "rating_Character",
          required: true,
          label: "Character",
          align: "center",
          field: row => row.rating_Character,
          format: val => (val ? val : "-"),
          sortable: true,
        },
        {
          name: "rating_Replay",
          required: true,
          label: "Replay",
          align: "center",
          field: row => row.rating_Replay,
          format: val => (val ? val : "-"),
          sortable: true,
        },
        {
          name: "rating_Experience",
          required: true,
          label: "Experience",
          align: "center",
          field: row => row.rating_Experience,
          format: val => (val ? val : "-"),
          sortable: true,
        },
        {
          name: "rating_Hang",
          required: true,
          label: "Hang",
          align: "center",
          field: row => row.rating_Hang,
          format: val => (val ? val : "-"),
          sortable: true,
        },
        {
          name: "rating_Setting",
          required: true,
          label: "Setting",
          align: "center",
          field: row => row.rating_Setting,
          format: val => (val ? val : "-"),
          sortable: true,
        },
        {
          name: "rating_Amenities",
          required: true,
          label: "Amenities",
          align: "center",
          field: row => row.rating_Amenities,
          format: val => (val ? val : "-"),
          sortable: true,
        },
        {
          name: "comments",
          required: true,
          label: "Comments",
          align: "left",
          field: row => row.comments,
          format: val => (val ? val : "-"),
        },
      ],
      courseRatingsDateFormatOptions: {
        year: "numeric",
        // using 2-digit as opposed to numeric will
        // force a leading zero if applicable
        day: "2-digit",
        month: "2-digit",
      },
      courseRatingsDateFormatter: null,
      ratingAttributes: [],
      courseRatings: [],
    };
  },
  created() {
    const options = this.courseRatingsDateFormatOptions;

    this.courseRatingsDateFormatter = new Intl.DateTimeFormat("en-US", options);
  },
  computed: {
    ...mapState(["idToken", "userProfiles"]),
    ...mapGetters(["isAdmin", "courses"]),
    readyForData() {
      const { idToken, courses } = this;
      const readyForData = !!idToken && courses.length > 0;

      return readyForData;
    },
  },
  methods: {
    getCourseById(id) {
      if (!id || !this.courses.length) {
        return {};
      }

      const course = this.courses.find(c => c.id === id);

      if (!course) {
        console.warn(`getCourseById: unable to find course ${id}`);
      }

      return course;
    },
    refreshData() {
      this.loading = true;

      this.$store
        .dispatch("fetchUserProfiles")
        .then(() => {
          const promises = [this.fetchAllCourseRatings()];

          return Promise.all(promises);
        })
        .then(([transformed]) => {
          this.courseRatings = transformed;
        })
        .catch(error => {
          this.loadError = error;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    fetchCourseRatingAttributesMeta() {
      const token = this.idToken;

      return getCourseRatingAttributesMeta(token).then(([, attributes]) => {
        this.ratingAttributes = attributes.map(a => {
          const attr = {
            ...a,
            rating: 0,
          };

          return attr;
        });
      });
    },
    fetchAllCourseRatings() {
      const token = this.idToken;

      return getAllCourseRatings(token).then(courseRatings => {
        const reviews = courseRatings.reviews;
        const transformed = this.transformCourseRatings(reviews);

        return transformed;
      });
    },
    getUserProfileById(uid) {
      return this.userProfiles[uid];
    },
    transformCourseRatings(input) {
      const transformed = input.map(review => {
        const { id, uid, courseId, createdAt, datePlayed, comments } = review;
        const datePlayedParsed = Date.parse(datePlayed);
        const datePlayedFormatted = this.courseRatingsDateFormatter.format(
          datePlayedParsed,
        );
        const createdAtParsed = Date.parse(createdAt);
        const createdAtFormatted = this.courseRatingsDateFormatter.format(
          createdAtParsed,
        );
        const course = this.getCourseById(courseId);
        const courseName = course.OLGDP001;
        const profile = this.getUserProfileById(uid);
        const ratings = review.UserCourseReview_CourseRatings.map(
          courseRating => {
            const { id, courseRatingAttributeId, rating } = courseRating;
            const attr = courseRating.CourseRatingAttribute;
            const name = attr.name;
            const newCourseRating = {
              id,
              uid,
              profile,
              courseRatingAttributeId,
              name,
              rating,
            };

            return newCourseRating;
          },
        );

        const newReview = {
          id,
          uid,
          courseId,
          courseName,
          createdAt,
          createdAtParsed,
          createdAtFormatted,
          datePlayed,
          datePlayedParsed,
          datePlayedFormatted,
          ratings,
          comments,
        };

        ratings.forEach(rating => {
          const key = `rating_${rating.name}`;
          const value = rating.rating;

          newReview[key] = value;
        });

        if (profile) {
          newReview.displayName = profile.displayName;
        }

        return newReview;
      });

      return transformed;
    },
  },
  watch: {
    readyForData: {
      handler(ready) {
        if (!ready) {
          return;
        }

        this.refreshData();
      },
      immediate: true,
    },
    idToken() {
      if (!this.courses.length) {
        return;
      }

      this.refreshData();
    },
  },
};
</script>
<style lang="scss" scoped>
.comments {
  white-space: pre-line;
  max-height: 200px;
  overflow: auto;
  min-width: 30ch;
  max-width: 60ch;
}
</style>
